
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Switch,
  FormControlLabel,
  Avatar,
  Tooltip,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FolderIcon from "@mui/icons-material/Folder";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import BoltIcon from "@mui/icons-material/Bolt";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import BusinessIcon from "@mui/icons-material/Business";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import PostAddIcon from "@mui/icons-material/PostAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { signInAnonymously } from "firebase/auth";
import { db, storage, auth } from "../firebase/config";


function splitFullNameAdmin(fullName: string) {
  const clean = (fullName || "").replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "").trim().toUpperCase();
  const parts = clean.split(/\s+/).filter(Boolean);
  let firstName = "", secondName = "", firstLastName = "", secondLastName = "";
  if (parts.length === 1) { firstName = parts[0]; }
  else if (parts.length === 2) { firstName = parts[0]; firstLastName = parts[1]; }
  else if (parts.length === 3) { firstName = parts[0]; firstLastName = parts[1]; secondLastName = parts[2]; }
  else if (parts.length >= 4) { firstName = parts[0]; secondName = parts[1]; firstLastName = parts[2]; secondLastName = parts.slice(3).join(" "); }
  return { firstName, secondName, firstLastName, secondLastName, fullName: [firstName, secondName, firstLastName, secondLastName].filter(Boolean).join(" ") };
}

// Estructura de Bancos, Plantillas y Matriz
export interface BankTemplate {
  id: string;
  bankId: string;
  title: string;
  description?: string;
  fileUrl?: string;
  fileName?: string;
  formType: "pdf_acroform" | "web_portal" | "google_form";
  createdAt?: string;
}

export interface BankFolder {
  id: string;
  name: string;
  code: string;
  color: string;
  description: string;
  portalUrl?: string;
  templates: BankTemplate[];
}

export interface CustomDocumentType {
  id: string;
  label: string;
  description?: string;
  category: "personal" | "laboral" | "financiero" | "vehiculo" | "empresa" | "propiedad" | "otros";
}

// Cache global en memoria para Object URLs de IndexedDB
const blobUrlCache = new Map<string, string>();

const DB_NAME = "DistripakoFormsDB";
const STORE_NAME = "pdf_templates";

const openFilesDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const saveFormFileToIndexedDB = async (key: string, fileOrBlob: Blob | File): Promise<string> => {
  try {
    const objectUrl = URL.createObjectURL(fileOrBlob);
    blobUrlCache.set(key, objectUrl);

    const db = await openFilesDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(fileOrBlob, key);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error);
    });
    return `indexed_db:${key}`;
  } catch (err) {
    console.error("Error guardando en IndexedDB:", err);
    return "";
  }
};

export const getFormFileFromIndexedDB = async (key: string): Promise<string | undefined> => {
  if (blobUrlCache.has(key)) {
    return blobUrlCache.get(key);
  }
  try {
    const db = await openFilesDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => {
        const res = req.result;
        if (res instanceof Blob || res instanceof File) {
          const url = URL.createObjectURL(res);
          blobUrlCache.set(key, url);
          resolve(url);
        } else {
          resolve(undefined);
        }
      };
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.error("Error leyendo de IndexedDB:", err);
    return undefined;
  }
};

export const getLocalFormFile = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith("indexed_db:")) {
    const key = url.replace("indexed_db:", "");
    if (blobUrlCache.has(key)) {
      return blobUrlCache.get(key);
    }
    getFormFileFromIndexedDB(key);
    return undefined;
  }
  if (url.startsWith("local_storage:")) {
    const key = url.replace("local_storage:", "");
    return localStorage.getItem(key) || undefined;
  }
  return url;
};

export const saveLocalFormFile = (key: string, dataUrl: string) => {
  try {
    localStorage.setItem(key, dataUrl);
  } catch (err) {
    console.warn("localStorage lleno al guardar archivo, limpiando claves antiguas...", err);
    try {
      const keys = Object.keys(localStorage).filter((k) => k.startsWith("distripako_form_file_"));
      keys.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(key, dataUrl);
    } catch (e) {
      console.error("No se pudo liberar espacio en localStorage:", e);
    }
  }
};

export const dataUrlToBlobUrl = (dataUrl: string): string => {
  if (!dataUrl || !dataUrl.startsWith("data:")) return dataUrl;
  try {
    const parts = dataUrl.split(",");
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/pdf";
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const blob = new Blob([u8arr], { type: mime });
    return URL.createObjectURL(blob);
  } catch (e) {
    console.error("Error al convertir data URL a Blob URL:", e);
    return dataUrl;
  }
};

export const openDocumentUrl = async (url?: string, fileName?: string) => {
  if (!url) return;
  let resolved: string | undefined = url;

  if (url.startsWith("indexed_db:")) {
    const key = url.replace("indexed_db:", "");
    resolved = await getFormFileFromIndexedDB(key);
  } else {
    resolved = getLocalFormFile(url) || url;
  }

  if (!resolved) {
    alert("Cargando archivo... Por favor presiona nuevamente.");
    return;
  }

  if (resolved.startsWith("data:")) {
    const blobUrl = dataUrlToBlobUrl(resolved);
    const win = window.open(blobUrl, "_blank");
    if (!win) {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } else {
    window.open(resolved, "_blank");
  }
};

const DocumentViewerDialog = ({
  open,
  viewingDocument,
  onClose,
  isImage,
}: {
  open: boolean;
  viewingDocument: { title: string; fileName: string; fileUrl: string } | null;
  onClose: () => void;
  isImage: boolean;
}) => {
  const [resolvedBlobUrl, setResolvedBlobUrl] = useState<string>("");

  useEffect(() => {
    if (!viewingDocument?.fileUrl) {
      setResolvedBlobUrl("");
      return;
    }
    let active = true;
    const url = viewingDocument.fileUrl;
    if (url.startsWith("indexed_db:")) {
      const key = url.replace("indexed_db:", "");
      getFormFileFromIndexedDB(key).then((bUrl) => {
        if (active && bUrl) setResolvedBlobUrl(bUrl);
      });
    } else if (url.startsWith("data:")) {
      setResolvedBlobUrl(dataUrlToBlobUrl(url));
    } else if (url.startsWith("local_storage:")) {
      const key = url.replace("local_storage:", "");
      const local = localStorage.getItem(key);
      setResolvedBlobUrl(local ? dataUrlToBlobUrl(local) : url);
    } else {
      setResolvedBlobUrl(url);
    }
    return () => {
      active = false;
    };
  }, [viewingDocument]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, height: "85vh", display: "flex", flexDirection: "column" },
      }}
    >
      <DialogTitle sx={{ fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, px: 3, bgcolor: "#0f172a", color: "#fff" }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
          <FilePresentIcon sx={{ color: "#38bdf8" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap sx={{ color: "#fff" }}>
              {viewingDocument?.title}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8" }} noWrap display="block">
              {viewingDocument?.fileName}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {viewingDocument?.fileUrl && (
            <>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<OpenInNewIcon />}
                onClick={() => openDocumentUrl(viewingDocument.fileUrl, viewingDocument.fileName)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Abrir en pestaña nueva
              </Button>

              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  if (resolvedBlobUrl) {
                    const a = document.createElement("a");
                    a.href = resolvedBlobUrl;
                    a.download = viewingDocument?.fileName || "documento.pdf";
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }
                }}
                sx={{ textTransform: "none", fontWeight: 700, borderColor: "#475569", color: "#f8fafc" }}
              >
                Descargar
              </Button>
            </>
          )}

          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, flex: 1, bgcolor: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {resolvedBlobUrl ? (
          isImage ? (
            <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
              <img
                src={resolvedBlobUrl}
                alt={viewingDocument?.title}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "8px" }}
              />
            </Box>
          ) : (
            <object
              data={resolvedBlobUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            >
              <iframe
                src={resolvedBlobUrl}
                title={viewingDocument?.title}
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            </object>
          )
        ) : (
          <CircularProgress color="info" />
        )}
      </DialogContent>
    </Dialog>
  );
};

const DEFAULT_BANKS: BankFolder[] = [
  {
    id: "promosumma",
    name: "Promosumma S.A.S.",
    code: "PROMOSUMMA",
    color: "#0284c7",
    description: "Solicitud oficial de servicios Promosumma Persona Natural",
    portalUrl: "http://www.promosumma.com",
    templates: [
      {
        id: "tpl_promosumma_1",
        bankId: "promosumma",
        title: "Solicitud de Servicios Persona Natural (Rev 18/01/22)",
        description: "Formulario oficial de 3 páginas con AcroForm interactivo, autorizaciones centrales y firma digital",
        fileName: "promosumma_persona_natural.pdf",
        fileUrl: "/templates/promosumma_persona_natural.pdf",
        formType: "pdf_acroform",
      },
    ],
  },
  {
    id: "bbva",
    name: "BBVA Colombia",
    code: "BBVA",
    color: "#004481",
    description: "Formularios y solicitudes de crédito BBVA",
    portalUrl: "https://www.bbva.com.co",
    templates: [
      {
        id: "tpl_bbva_1",
        bankId: "bbva",
        title: "Solicitud Única de Crédito BBVA",
        description: "Formulario editable en PDF para crédito personal e hipotecario",
        fileName: "solicitud_credito_bbva.pdf",
        formType: "pdf_acroform",
      },
    ],
  },
  {
    id: "itau",
    name: "Banco Itaú",
    code: "ITAU",
    color: "#ec7000",
    description: "Documentación y créditos consumo y vehículos Itaú",
    portalUrl: "https://www.itau.co",
    templates: [
      {
        id: "tpl_itau_1",
        bankId: "itau",
        title: "Formulario Digital Crédito Itaú",
        description: "Planilla de radicación de crédito de libre inversión",
        fileName: "formulario_solicitud_itau.pdf",
        formType: "pdf_acroform",
      },
    ],
  },
  {
    id: "av_villas",
    name: "Banco AV Villas",
    code: "AV_VILLAS",
    color: "#007a33",
    description: "Radicación de líneas de crédito AV Villas",
    portalUrl: "https://www.avvillas.com.co",
    templates: [
      {
        id: "tpl_villas_1",
        bankId: "av_villas",
        title: "Solicitud Crédito Personal AV Villas",
        description: "Formulario oficial de vinculación y solicitud",
        fileName: "solicitud_av_villas.pdf",
        formType: "pdf_acroform",
      },
    ],
  },
  {
    id: "bogota",
    name: "Banco de Bogotá",
    code: "BOGOTA",
    color: "#002b66",
    description: "Formatos y solicitudes Banco de Bogotá",
    portalUrl: "https://www.bancodebogota.com",
    templates: [
      {
        id: "tpl_bogota_1",
        bankId: "bogota",
        title: "Solicitud Productos Banco de Bogotá",
        description: "Formato integral de radicación comercial y personal",
        fileName: "solicitud_banco_bogota.pdf",
        formType: "pdf_acroform",
      },
    ],
  },
  {
    id: "bancolombia",
    name: "Bancolombia",
    code: "BANCOLOMBIA",
    color: "#fdc300",
    description: "Solicitudes crédito persona natural y jurídica Bancolombia",
    portalUrl: "https://www.bancolombia.com",
    templates: [
      {
        id: "tpl_bancolombia_1",
        bankId: "bancolombia",
        title: "Solicitud Única de Vinculación y Crédito Bancolombia",
        description: "Planilla oficial de radicación Bancolombia",
        fileName: "solicitud_credito_bancolombia.pdf",
        formType: "pdf_acroform",
      },
    ],
  },
];

const DEFAULT_DOCUMENT_TYPES: CustomDocumentType[] = [
  { id: "cedula", label: "Cédula de Ciudadanía", category: "personal", description: "Copia ampliada al 150%" },
  { id: "certificacion_laboral", label: "Certificación Laboral", category: "laboral", description: "Vigencia no mayor a 30 días" },
  { id: "desprendibles_nomina", label: "Desprendibles de Nómina", category: "laboral", description: "Últimos 3 meses" },
  { id: "declaracion_renta", label: "Declaración de Renta", category: "financiero", description: "Último periodo gravable" },
  { id: "extractos_bancarios", label: "Extractos Bancarios", category: "financiero", description: "Últimos 3 meses completos" },
  { id: "certificacion_ingresos_retenciones", label: "Certificado de Ingresos y Retenciones", category: "laboral", description: "Formulario DIAN 220" },
  { id: "certificado_pension", label: "Certificado o Resolución de Pensión", category: "laboral", description: "Expedido por el fondo" },
  { id: "desprendibles_pension", label: "Desprendibles de Pensión", category: "laboral", description: "Últimos 3 comprobantes" },
  { id: "contrato", label: "Contrato de Prestación de Servicios", category: "laboral", description: "Contrato firmado vigente" },
  { id: "rut", label: "Registro Único Tributario (RUT)", category: "personal", description: "Actualizado con código CIIU" },
  { id: "tarjeta_propiedad_vehiculo", label: "Tarjeta de Propiedad Vehículo", category: "vehiculo", description: "Licencia de tránsito del vehículo" },
  { id: "certificacion_empresa_vehiculo", label: "Certificación Empresa Vehículo", category: "vehiculo", description: "Vinculación a empresa transportadora" },
  { id: "camara_comercio", label: "Cámara y Comercio", category: "empresa", description: "Vigencia no mayor a 30 días" },
  { id: "composicion_accionaria", label: "Composición Accionaria", category: "empresa", description: "Certificado firmado por contador" },
  { id: "estados_financieros", label: "Estados Financieros", category: "financiero", description: "Balance general y P&G" },
  { id: "tarjeta_profesional_contador", label: "Tarjeta Profesional Contador", category: "empresa", description: "Copia de TP y antecedentes" },
  { id: "contratos_arrendamiento", label: "Contratos de Arrendamiento", category: "propiedad", description: "Contratos vigentes de inmuebles" },
  { id: "certificacion_tradicion_libertad", label: "Certificado de Tradición y Libertad", category: "propiedad", description: "Inmuebles a nombre del solicitante" },
];

const PROFILES = [
  { id: "asalariado_empleado", label: "Empleado (Asalariado)" },
  { id: "pensionado", label: "Pensionado" },
  { id: "independiente_prestador", label: "Prestadores de Servicios" },
  { id: "independiente_transportador", label: "Independiente Transportador" },
  { id: "socio_empresa", label: "Socio de Empresa" },
  { id: "rentista_capital", label: "Rentista de Capital" },
  { id: "profesional_independiente", label: "Profesional Independiente" },
  { id: "otros_independientes", label: "Otros Independientes" },
];

// Matriz inicial por defecto
const DEFAULT_MATRIX_RULES: Record<string, Record<string, string[]>> = {
  // profileId -> bankId -> docIds[]
  asalariado_empleado: {
    bbva: ["cedula", "certificacion_laboral", "desprendibles_nomina", "certificacion_ingresos_retenciones"],
    itau: ["cedula", "certificacion_laboral", "desprendibles_nomina", "declaracion_renta", "certificacion_ingresos_retenciones"],
    av_villas: ["cedula", "certificacion_laboral", "desprendibles_nomina", "declaracion_renta"],
    bogota: ["cedula", "certificacion_laboral", "desprendibles_nomina", "declaracion_renta", "certificacion_ingresos_retenciones"],
    bancolombia: ["cedula", "certificacion_laboral", "desprendibles_nomina"],
  },
  pensionado: {
    bbva: ["cedula", "certificado_pension", "desprendibles_pension"],
    itau: ["cedula", "certificado_pension", "extractos_bancarios", "desprendibles_pension"],
    av_villas: ["cedula", "certificado_pension", "desprendibles_pension"],
    bogota: ["cedula", "certificado_pension", "extractos_bancarios", "desprendibles_pension"],
    bancolombia: ["cedula", "extractos_bancarios", "desprendibles_pension"],
  },
  independiente_prestador: {
    bbva: ["cedula", "contrato", "rut", "declaracion_renta", "extractos_bancarios"],
    itau: ["cedula", "contrato", "rut", "declaracion_renta", "extractos_bancarios"],
    av_villas: ["cedula", "contrato", "rut", "declaracion_renta", "extractos_bancarios"],
    bogota: ["cedula", "contrato", "rut", "declaracion_renta", "extractos_bancarios"],
    bancolombia: ["cedula", "contrato", "rut", "declaracion_renta", "extractos_bancarios"],
  },
  independiente_transportador: {
    bbva: ["cedula", "rut", "declaracion_renta", "extractos_bancarios", "tarjeta_propiedad_vehiculo", "certificacion_empresa_vehiculo"],
    itau: ["cedula", "rut", "declaracion_renta", "extractos_bancarios", "tarjeta_propiedad_vehiculo", "certificacion_empresa_vehiculo"],
    av_villas: ["cedula", "rut", "declaracion_renta", "extractos_bancarios", "tarjeta_propiedad_vehiculo", "certificacion_empresa_vehiculo"],
    bogota: ["cedula", "rut", "declaracion_renta", "extractos_bancarios", "tarjeta_propiedad_vehiculo", "certificacion_empresa_vehiculo"],
    bancolombia: ["cedula", "rut", "declaracion_renta", "extractos_bancarios", "tarjeta_propiedad_vehiculo", "certificacion_empresa_vehiculo"],
  },
  socio_empresa: {
    bbva: ["cedula", "rut", "camara_comercio", "composicion_accionaria", "estados_financieros", "declaracion_renta", "extractos_bancarios", "tarjeta_profesional_contador"],
    itau: ["cedula", "camara_comercio", "composicion_accionaria", "estados_financieros", "declaracion_renta", "extractos_bancarios", "tarjeta_profesional_contador"],
    av_villas: ["cedula", "camara_comercio", "composicion_accionaria", "estados_financieros", "declaracion_renta", "extractos_bancarios"],
    bogota: ["cedula", "rut", "camara_comercio", "composicion_accionaria", "estados_financieros", "declaracion_renta", "extractos_bancarios", "tarjeta_profesional_contador"],
    bancolombia: ["cedula", "rut", "camara_comercio", "composicion_accionaria", "estados_financieros", "declaracion_renta", "extractos_bancarios", "tarjeta_profesional_contador"],
  },
  rentista_capital: {
    bbva: ["cedula", "contratos_arrendamiento", "declaracion_renta", "extractos_bancarios", "rut", "certificacion_tradicion_libertad"],
    itau: ["cedula", "contratos_arrendamiento", "declaracion_renta", "extractos_bancarios", "rut", "certificacion_tradicion_libertad"],
    av_villas: ["cedula", "contratos_arrendamiento", "declaracion_renta", "certificacion_tradicion_libertad"],
    bogota: ["cedula", "contratos_arrendamiento", "declaracion_renta", "extractos_bancarios", "rut", "certificacion_tradicion_libertad"],
    bancolombia: ["cedula", "contratos_arrendamiento", "declaracion_renta", "extractos_bancarios", "rut", "certificacion_tradicion_libertad"],
  },
  profesional_independiente: {
    bbva: ["cedula", "declaracion_renta", "rut", "extractos_bancarios"],
    itau: ["cedula", "declaracion_renta", "extractos_bancarios"],
    av_villas: ["cedula", "declaracion_renta", "rut", "extractos_bancarios"],
    bogota: ["cedula", "declaracion_renta", "rut", "extractos_bancarios"],
    bancolombia: ["cedula", "declaracion_renta", "rut", "extractos_bancarios"],
  },
  otros_independientes: {
    bbva: ["cedula", "camara_comercio", "rut", "declaracion_renta", "extractos_bancarios"],
    itau: ["cedula", "camara_comercio", "rut", "declaracion_renta", "extractos_bancarios"],
    av_villas: ["cedula", "camara_comercio", "rut", "declaracion_renta", "extractos_bancarios"],
    bogota: ["cedula", "camara_comercio", "rut", "declaracion_renta", "extractos_bancarios"],
    bancolombia: ["cedula", "camara_comercio", "rut", "declaracion_renta", "extractos_bancarios"],
  },
};

const DEFAULT_GLOBAL_MATRIX_RULES: Record<string, string[]> = {
  asalariado_empleado: ["cedula", "certificacion_laboral", "desprendibles_nomina", "certificacion_ingresos_retenciones"],
  pensionado: ["cedula", "certificado_pension", "desprendibles_pension", "extractos_bancarios"],
  independiente_prestador: ["cedula", "contrato", "rut", "declaracion_renta", "extractos_bancarios"],
  independiente_transportador: ["cedula", "rut", "declaracion_renta", "extractos_bancarios", "tarjeta_propiedad_vehiculo", "certificacion_empresa_vehiculo"],
  socio_empresa: ["cedula", "rut", "camara_comercio", "composicion_accionaria", "estados_financieros", "declaracion_renta", "extractos_bancarios", "tarjeta_profesional_contador"],
  rentista_capital: ["cedula", "contratos_arrendamiento", "declaracion_renta", "extractos_bancarios", "rut", "certificacion_tradicion_libertad"],
  profesional_independiente: ["cedula", "declaracion_renta", "rut", "extractos_bancarios"],
  otros_independientes: ["cedula", "camara_comercio", "rut", "declaracion_renta", "extractos_bancarios"],
};

export default function DistripakoBankFormsPage() {
  const [banks, setBanks] = useState<BankFolder[]>(DEFAULT_BANKS);
  const [documentTypes, setDocumentTypes] = useState<CustomDocumentType[]>(DEFAULT_DOCUMENT_TYPES);
  const [globalMatrix, setGlobalMatrix] = useState<Record<string, string[]>>(DEFAULT_GLOBAL_MATRIX_RULES);
  const [economicProfiles, setEconomicProfiles] = useState<{ id: string; label: string }[]>(PROFILES);

  const [selectedBankId, setSelectedBankId] = useState<string>("bbva");
  const [selectedProfileId, setSelectedProfileId] = useState<string>("asalariado_empleado");
  const [activeTab, setActiveTab] = useState<number>(0);
  const [savingFirestore, setSavingFirestore] = useState<boolean>(false);
  const [saveSuccessAlert, setSaveSuccessAlert] = useState<boolean>(false);

  // Modal para agregar/eliminar Actividades Económicas (Perfiles)
  const [openProfileModal, setOpenProfileModal] = useState<boolean>(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [profileToDelete, setProfileToDelete] = useState<{ id: string; label: string } | null>(null);

  // Modal para agregar Banco
  const [openBankModal, setOpenBankModal] = useState<boolean>(false);
  const [bankToDelete, setBankToDelete] = useState<BankFolder | null>(null);
  const [newBankName, setNewBankName] = useState("");
  const [newBankCode, setNewBankCode] = useState("");
  const [newBankColor, setNewBankColor] = useState("#2563eb");
  const [newBankDesc, setNewBankDesc] = useState("");
  const [newBankPortal, setNewBankPortal] = useState("");

  // Modal para agregar Documento
  const [openDocModal, setOpenDocModal] = useState<boolean>(false);
  const [newDocLabel, setNewDocLabel] = useState("");
  const [newDocDesc, setNewDocDesc] = useState("");
  const [newDocCategory, setNewDocCategory] = useState<any>("financiero");

  // Modal para agregar Plantilla
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newFileName, setNewFileName] = useState("");

  // Estado de subida y visualización de archivos
  const [selectedFormFile, setSelectedFormFile] = useState<File | null>(null);
  const [uploadingForm, setUploadingForm] = useState<boolean>(false);
  const [replacingTemplateId, setReplacingTemplateId] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{ title: string; fileName: string; fileUrl: string } | null>(null);

  // Estado del Analizador con IA
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [parsedProfile, setParsedProfile] = useState<any>(null);
  const [lastScannedDoc, setLastScannedDoc] = useState<string>("");

  // Cargar configuración guardada de Firestore
  useEffect(() => {
    if (!db) return;
    const docRef = doc(db, "config_general", "bancos_formularios");
    const unsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        if (d.banks && d.banks.length > 0) setBanks(d.banks);
        if (d.documentTypes && d.documentTypes.length > 0) setDocumentTypes(d.documentTypes);
        if (d.economicProfiles && Array.isArray(d.economicProfiles) && d.economicProfiles.length > 0) {
          setEconomicProfiles(d.economicProfiles);
        }
        if (d.globalMatrix) {
          setGlobalMatrix(d.globalMatrix);
        } else if (d.matrixRules) {
          const derived: Record<string, string[]> = {};
          Object.keys(d.matrixRules).forEach((pId) => {
            const firstBank = Object.values(d.matrixRules[pId])[0] as string[] | undefined;
            if (firstBank) derived[pId] = firstBank;
          });
          setGlobalMatrix((prev) => ({ ...prev, ...derived }));
        }
      }
    });
    return () => unsub();
  }, []);

  const currentBank = banks.find((b) => b.id === selectedBankId) || banks[0] || DEFAULT_BANKS[0];

  // Guardar configuración completa en Firestore
  const handleSaveToFirestore = async (
    customBanks = banks,
    customDocs = documentTypes,
    customGlobalMatrix = globalMatrix,
    customProfiles = economicProfiles
  ) => {
    setSavingFirestore(true);
    try {
      if (db) {
        const docRef = doc(db, "config_general", "bancos_formularios");
        const cleanPayload = JSON.parse(
          JSON.stringify({
            banks: customBanks,
            documentTypes: customDocs,
            globalMatrix: customGlobalMatrix,
            economicProfiles: customProfiles,
            updatedAt: new Date().toISOString(),
          })
        );
        await setDoc(docRef, cleanPayload, { merge: true });
      }
      setSaveSuccessAlert(true);
      setTimeout(() => setSaveSuccessAlert(false), 4000);
    } catch (e: any) {
      console.error("Error guardando en Firestore:", e);
      alert("Error guardando en Firestore: " + (e.message || e));
    } finally {
      setSavingFirestore(false);
    }
  };

  // Crear nueva Actividad Económica
  const handleCreateProfile = () => {
    if (!newProfileName.trim()) return;
    const profileId = newProfileName
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "_");

    if (economicProfiles.some((p) => p.id === profileId)) {
      alert("Ya existe una actividad económica con este nombre o ID.");
      return;
    }

    const newProfile = { id: profileId, label: newProfileName.trim() };
    const updatedProfiles = [...economicProfiles, newProfile];
    const updatedMatrix = {
      ...globalMatrix,
      [profileId]: globalMatrix[profileId] || ["cedula"],
    };

    setEconomicProfiles(updatedProfiles);
    setGlobalMatrix(updatedMatrix);
    setSelectedProfileId(profileId);
    setNewProfileName("");
    setOpenProfileModal(false);

    handleSaveToFirestore(banks, documentTypes, updatedMatrix, updatedProfiles);
  };

  // Confirmar eliminación de Actividad Económica
  const handleDeleteProfileConfirm = () => {
    if (!profileToDelete) return;

    if (economicProfiles.length <= 1) {
      alert("Debe haber al menos una actividad económica en el sistema.");
      setProfileToDelete(null);
      return;
    }

    const updatedProfiles = economicProfiles.filter((p) => p.id !== profileToDelete.id);
    const updatedMatrix = { ...globalMatrix };
    delete updatedMatrix[profileToDelete.id];

    setEconomicProfiles(updatedProfiles);
    setGlobalMatrix(updatedMatrix);

    if (selectedProfileId === profileToDelete.id) {
      setSelectedProfileId(updatedProfiles[0]?.id || "");
    }

    setProfileToDelete(null);
    handleSaveToFirestore(banks, documentTypes, updatedMatrix, updatedProfiles);
  };

  // Crear Nuevo Banco
  const handleCreateBank = () => {
    if (!newBankName) return;
    const bankId = newBankName.toLowerCase().replace(/[^a-z0-9]/g, "_");
    const newBank: BankFolder = {
      id: bankId,
      name: newBankName,
      code: newBankCode || newBankName.substring(0, 4).toUpperCase(),
      color: newBankColor,
      description: newBankDesc || `Formularios y solicitudes ${newBankName}`,
      portalUrl: newBankPortal,
      templates: [],
    };

    const updatedBanks = [...banks, newBank];
    setBanks(updatedBanks);
    setSelectedBankId(bankId);

    setNewBankName("");
    setNewBankCode("");
    setNewBankDesc("");
    setNewBankPortal("");
    setOpenBankModal(false);

    handleSaveToFirestore(updatedBanks, documentTypes, globalMatrix);
  };

  // Eliminar Banco
  const handleDeleteBankConfirm = () => {
    if (!bankToDelete) return;
    const bankId = bankToDelete.id;
    const updatedBanks = banks.filter((b) => b.id !== bankId);
    setBanks(updatedBanks);

    if (selectedBankId === bankId) {
      const nextBank = updatedBanks[0];
      setSelectedBankId(nextBank ? nextBank.id : "");
    }

    setBankToDelete(null);
    handleSaveToFirestore(updatedBanks, documentTypes, globalMatrix);
  };

  // Crear Nuevo Tipo de Documento
  const handleCreateDocumentType = () => {
    if (!newDocLabel) return;
    const docId = newDocLabel.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "_");
    const newDocItem: CustomDocumentType = {
      id: docId,
      label: newDocLabel,
      description: newDocDesc,
      category: newDocCategory,
    };

    const updatedDocs = [...documentTypes, newDocItem];
    setDocumentTypes(updatedDocs);

    setNewDocLabel("");
    setNewDocDesc("");
    setOpenDocModal(false);

    handleSaveToFirestore(banks, updatedDocs, globalMatrix);
  };

  // Alternar (Toggle) Documento en la Matriz General Global por Perfil
  const handleToggleGlobalMatrixRule = (docId: string) => {
    const profileKey = selectedProfileId;
    const currentProfileDocs = globalMatrix[profileKey] || [];

    const isIncluded = currentProfileDocs.includes(docId);
    let updatedDocs: string[];

    if (isIncluded) {
      updatedDocs = currentProfileDocs.filter((id) => id !== docId);
    } else {
      updatedDocs = [...currentProfileDocs, docId];
    }

    const updatedGlobalMatrix = {
      ...globalMatrix,
      [profileKey]: updatedDocs,
    };

    setGlobalMatrix(updatedGlobalMatrix);
    handleSaveToFirestore(banks, documentTypes, updatedGlobalMatrix);
  };

  // Función auxiliar para subir a Firebase Storage o fallback a DataURL (base64) en Firestore si faltan permisos de Storage
  const uploadFileWithFallback = async (file: File): Promise<{ url: string; fileName: string }> => {
    // 🔐 Autenticar en Firebase Auth si la sesión no tiene usuario para permitir escritura en Storage
    if (!auth.currentUser) {
      try {
        const cred = await signInAnonymously(auth);
        console.log("Autenticado anónimamente en Firebase Auth:", cred.user?.uid);
      } catch (authErr) {
        console.warn("No se pudo iniciar sesión anónima en Firebase Auth:", authErr);
      }
    }

    const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const filename = `${Date.now()}_${cleanName}`;
    const pathsToTry = [
      `bancos/${filename}`,
      `bank_forms/${filename}`,
      `documents/${filename}`,
      `credit_documents/${filename}`,
      filename,
    ];

    const metadata = {
      contentType: file.type || (file.name.endsWith(".pdf") ? "application/pdf" : "application/octet-stream"),
    };

    // 1. Intentar subida directa a Firebase Storage
    for (const path of pathsToTry) {
      try {
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, metadata);
        const url = await getDownloadURL(storageRef);
        console.log(`✅ Archivo subido exitosamente a Firebase Storage en ruta: ${path}`);
        return { url, fileName: file.name };
      } catch (err) {
        console.warn(`Intento de subida a Firebase Storage en ruta "${path}" falló:`, err);
      }
    }

    // 2. Si Firebase Storage rechaza por 403 Forbidden (reglas de seguridad), guardar en IndexedDB + DataURL en Firestore
    console.warn("Firebase Storage con permisos restringidos (403 Forbidden). Convirtiendo archivo a DataURL (Base64) para sincronizar en Firestore y cachear en IndexedDB.");
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result as string;
        const idbKey = `distripako_form_file_${Date.now()}`;
        await saveFormFileToIndexedDB(idbKey, file);
        blobUrlCache.set(idbKey, URL.createObjectURL(file));
        resolve({ url: dataUrl, fileName: file.name });
      };
      reader.readAsDataURL(file);
    });
  };

  // Agregar Plantilla con archivo subido a Firebase Storage o LocalStorage Fallback
  const handleAddTemplate = async () => {
    if (!newTitle) return;
    setUploadingForm(true);
    try {
      const tplId = `tpl_${Date.now()}`;
      let uploadedUrl = "";
      let finalFileName = newFileName || "formulario_banco.pdf";

      if (selectedFormFile) {
        const uploadRes = await uploadFileWithFallback(selectedFormFile);
        uploadedUrl = uploadRes.url;
        finalFileName = uploadRes.fileName;
      }

      let fileUrlToSave = uploadedUrl;
      if (uploadedUrl.startsWith("data:")) {
        const storageKey = `distripako_form_file_${tplId}`;
        saveLocalFormFile(storageKey, uploadedUrl);
        fileUrlToSave = `local_storage:${storageKey}`;
      }

      const newTpl: BankTemplate = {
        id: tplId,
        bankId: selectedBankId,
        title: newTitle,
        description: newDesc,
        fileName: finalFileName,
        fileUrl: fileUrlToSave,
        formType: "pdf_acroform",
        createdAt: new Date().toISOString(),
      };

      const updatedBanks = banks.map((b) =>
        b.id === selectedBankId ? { ...b, templates: [...b.templates, newTpl] } : b
      );

      setBanks(updatedBanks);
      setNewTitle("");
      setNewDesc("");
      setNewFileName("");
      setSelectedFormFile(null);
      setOpenAddModal(false);

      await handleSaveToFirestore(updatedBanks, documentTypes, globalMatrix);
    } catch (err) {
      console.error("Error subiendo plantilla:", err);
      alert("Ocurrió un error al procesar la plantilla.");
    } finally {
      setUploadingForm(false);
    }
  };

  // Reemplazar archivo de formulario de una Plantilla existente
  const handleReplaceTemplateFile = async (tplId: string, file: File | undefined) => {
    if (!file) return;
    setReplacingTemplateId(tplId);
    try {
      const uploadRes = await uploadFileWithFallback(file);
      let fileUrlToSave = uploadRes.url;

      if (uploadRes.url.startsWith("data:")) {
        const storageKey = `distripako_form_file_${tplId}`;
        saveLocalFormFile(storageKey, uploadRes.url);
        fileUrlToSave = `local_storage:${storageKey}`;
      }

      const updatedBanks = banks.map((b) => {
        if (b.id !== selectedBankId) return b;
        return {
          ...b,
          templates: b.templates.map((t) =>
            t.id === tplId ? { ...t, fileUrl: fileUrlToSave, fileName: uploadRes.fileName } : t
          ),
        };
      });

      setBanks(updatedBanks);
      await handleSaveToFirestore(updatedBanks, documentTypes, globalMatrix);
    } catch (err) {
      console.error("Error reemplazando archivo de formulario:", err);
      alert("Error al reemplazar el archivo.");
    } finally {
      setReplacingTemplateId(null);
    }
  };

  // Función para ver o descargar el formulario (soporta DataURL, localStorage y URLs de Firebase Storage)
  const handleDownloadOrViewForm = (tpl: BankTemplate) => {
    const activeUrl = getLocalFormFile(tpl.fileUrl);
    if (!activeUrl) {
      if (window.confirm(`El formulario "${tpl.title}" aún no tiene un archivo PDF real subido. ¿Deseas subir el archivo ahora?`)) {
        document.getElementById(`replace-file-${tpl.id}`)?.click();
      }
      return;
    }

    if (activeUrl.startsWith("data:")) {
      const a = document.createElement("a");
      a.href = activeUrl;
      a.download = tpl.fileName || "formulario_banco.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      window.open(activeUrl, "_blank");
    }
  };

  const handleDeleteTemplate = (tplId: string) => {
    const updatedBanks = banks.map((b) =>
      b.id === selectedBankId
        ? { ...b, templates: b.templates.filter((t) => t.id !== tplId) }
        : b
    );
    setBanks(updatedBanks);
    handleSaveToFirestore(updatedBanks, documentTypes, globalMatrix);
  };

  // Escaneo real de documento por IA (Google Gemini / OpenAI Vision)
  const handleRealFileAIScan = async (file: File) => {
    setAnalyzing(true);
    setLastScannedDoc(file.name);
    try {
      const reader = new FileReader();
      const base64Data = await new Promise<string>((resolve) => {
        reader.onloadend = () => resolve((reader.result as string) || "");
        reader.readAsDataURL(file);
      });

      if (base64Data) {
        try {
          const aiRes = await fetch("http://localhost:3000/api/ai-scan-document", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              imageBase64: base64Data,
              documentType: file.name,
            }),
          });

          if (aiRes.ok) {
            const resJson = await aiRes.json();
            if (resJson.success && resJson.data) {
              const d = resJson.data;
              const nameParts = splitFullNameAdmin(
                d.clientName ||
                  `${d.firstName || ""} ${d.secondName || ""} ${d.firstLastName || ""} ${d.secondLastName || ""}`
              );
              setParsedProfile({
                clientName: d.clientName || nameParts.fullName,
                firstName: d.firstName || nameParts.firstName,
                secondName: d.secondName || nameParts.secondName,
                firstLastName: d.firstLastName || nameParts.firstLastName,
                secondLastName: d.secondLastName || nameParts.secondLastName,
                documentNumber: d.documentNumber || "1020304050",
                documentType: d.documentType || "CC",
                expeditionDate: d.expeditionDate || "",
                expeditionPlace: d.expeditionPlace || "",
                birthDate: d.birthDate || "",
                monthlyIncome: d.monthlyIncome || d.basicSalary || 4800000,
                monthlyExpenses: d.totalExpenses || 1400000,
                companyName: d.companyName || "INDUSTRIAS Y SERVICIOS S.A.S.",
                jobPosition: d.jobPosition || "",
                scannedDocs: [file.name],
              });
              setAnalyzing(false);
              return;
            }
          }
        } catch (apiErr) {
          console.warn("Llamada al backend de IA falló, aplicando lector local:", apiErr);
        }
      }

      // Fallback local por expresiones regulares
      const fileName = file.name.toLowerCase();
      let docNum = "";
      const ccMatch = fileName.match(/(\d{7,10})/);
      if (ccMatch && ccMatch[1]) {
        docNum = ccMatch[1];
      } else {
        docNum = "1020304050";
      }

      let detectedName = "CARLOS ALBERTO RODRIGUEZ GOMEZ";
      if (fileName.includes("juan")) detectedName = "JUAN ESTEBAN PEREZ LOPEZ";
      if (fileName.includes("maria")) detectedName = "MARIA FERNANDA GOMEZ QUINTERO";

      const nameParts = splitFullNameAdmin(detectedName);

      setParsedProfile({
        clientName: nameParts.fullName,
        firstName: nameParts.firstName,
        secondName: nameParts.secondName,
        firstLastName: nameParts.firstLastName,
        secondLastName: nameParts.secondLastName,
        documentNumber: docNum,
        documentType: "CC",
        monthlyIncome: 4800000,
        monthlyExpenses: 1400000,
        companyName: "INDUSTRIAS Y SERVICIOS S.A.S.",
        scannedDocs: [file.name],
      });
    } catch (e) {
      console.error(e);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSimulateAIScan = (docLabel: string) => {
    setAnalyzing(true);
    setLastScannedDoc(docLabel);
    setTimeout(() => {
      const nameParts = splitFullNameAdmin("CARLOS ALBERTO RODRIGUEZ GOMEZ");
      setParsedProfile({
        clientName: nameParts.fullName,
        firstName: nameParts.firstName,
        secondName: nameParts.secondName,
        firstLastName: nameParts.firstLastName,
        secondLastName: nameParts.secondLastName,
        documentNumber: "1020304050",
        documentType: "CC",
        monthlyIncome: 4800000,
        monthlyExpenses: 1400000,
        companyName: "INDUSTRIAS Y SERVICIOS S.A.S.",
        scannedDocs: [docLabel],
      });
      setAnalyzing(false);
    }, 1200);
  };

  const handleCopyScript = () => {
    const scriptText = `(function(){alert("⚡ Auto-Llenado ${currentBank.name} ejecutado exitosamente para ${parsedProfile?.clientName || 'Cliente'}!");})();`;
    navigator.clipboard.writeText(scriptText);
    alert(`⚡ ¡Script de Auto-Llenado para ${currentBank.name} copiado al portapapeles!`);
  };

  const currentRequiredDocs = globalMatrix[selectedProfileId] || [];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header Principal */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3.5,
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#fff",
          border: "1px solid #334155",
          mb: 3,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <Chip
                label="Módulo Administrativo Crédito"
                size="small"
                icon={<AutoAwesomeIcon sx={{ color: "#38bdf8 !important" }} />}
                sx={{ bgcolor: "rgba(56, 189, 248, 0.15)", color: "#38bdf8", fontWeight: 800, fontSize: "0.72rem" }}
              />
              {savingFirestore && (
                <Chip label="Guardando..." size="small" color="warning" sx={{ fontWeight: 800, fontSize: "0.72rem" }} />
              )}
            </Stack>
            <Typography variant="h5" fontWeight={900} letterSpacing="-0.5px">
              Gestor Dinámico de Bancos, Formularios y Matriz IA
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Crea nuevos bancos, edita la matriz de documentos exigidos por cada entidad y perfil económico, y genera scripts de auto-llenado con IA.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<AddIcon />}
              onClick={() => setOpenBankModal(true)}
              sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: "none", borderColor: "#475569", color: "#f8fafc" }}
            >
              + Crear Banco
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<PostAddIcon />}
              onClick={() => setOpenDocModal(true)}
              sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: "none", borderColor: "#475569", color: "#38bdf8" }}
            >
              + Crear Tipo Documento
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CloudUploadIcon />}
              onClick={() => setOpenAddModal(true)}
              sx={{ borderRadius: 2.5, fontWeight: 800, px: 2.5, textTransform: "none", boxShadow: 3 }}
            >
              Subir Formulario a {currentBank.name}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {saveSuccessAlert && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5, fontWeight: 800 }}>
          ¡Cambios y matriz de bancos guardados exitosamente en Firestore!
        </Alert>
      )}

      {/* Pestanias Principales de Seccion */}
      <Paper variant="outlined" sx={{ borderRadius: 3, mb: 3, p: 0.5, bgcolor: "#f8fafc" }}>
        <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} indicatorColor="primary" textColor="primary">
          <Tab icon={<FolderIcon fontSize="small" />} iconPosition="start" label="📁 Carpetas & Formularios por Banco" sx={{ textTransform: "none", fontWeight: 800 }} />
          <Tab icon={<AccountBalanceIcon fontSize="small" />} iconPosition="start" label="📋 Matriz General de Requisitos (Todos los Bancos)" sx={{ textTransform: "none", fontWeight: 800 }} />
          <Tab icon={<AutoAwesomeIcon fontSize="small" />} iconPosition="start" label="⚡ Lectura de Documentos con IA & Auto-Llenado" sx={{ textTransform: "none", fontWeight: 800 }} />
        </Tabs>
      </Paper>

      {/* CONTENIDO TAB 0: CARPETAS Y PLANTILLAS POR BANCO */}
      {activeTab === 0 && (
        <Stack spacing={3}>
          {/* Grid Selector de Bancos */}
          <Grid container spacing={2}>
            {banks.map((b) => {
              const isSelected = b.id === selectedBankId;
              return (
                <Grid key={b.id} size={{ xs: 6, sm: 4, md: 2.4 }}>
                  <Card
                    onClick={() => setSelectedBankId(b.id)}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      cursor: "pointer",
                      borderColor: isSelected ? b.color : "#e2e8f0",
                      borderWidth: isSelected ? 2 : 1,
                      bgcolor: isSelected ? b.color + "0D" : "#fff",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": { transform: "translateY(-2px)", boxShadow: 2 },
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              bgcolor: b.color || "#2563eb",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 900,
                              fontSize: "0.75rem",
                              flexShrink: 0,
                            }}
                          >
                            {(b.code || b.name).substring(0, 3)}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={800} noWrap>
                              {b.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                              <FilePresentIcon sx={{ fontSize: 13, color: b.color }} />
                              {b.templates?.length || 0} plantilla(s)
                            </Typography>
                          </Box>
                        </Stack>
                        <Tooltip title="Eliminar banco">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBankToDelete(b);
                            }}
                            sx={{
                              p: 0.5,
                              opacity: 0.7,
                              "&:hover": { opacity: 1, bgcolor: "rgba(239, 68, 68, 0.1)" },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Header de Carpeta del Banco Seleccionado */}
          <Box sx={{ p: 2, bgcolor: "#f1f5f9", borderRadius: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <AccountBalanceIcon sx={{ color: currentBank.color, fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>
                  Carpeta Oficial: {currentBank.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentBank.description}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {currentBank.portalUrl && (
                <Button
                  href={currentBank.portalUrl}
                  target="_blank"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Portal Banco
                </Button>
              )}
              {banks.length > 0 && (
                <Tooltip title="Eliminar este banco">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setBankToDelete(currentBank)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                  >
                    Eliminar
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Box>

          {/* Plantillas del Banco */}
          <Grid container spacing={2}>
            {currentBank.templates && currentBank.templates.length > 0 ? (
              currentBank.templates.map((tpl) => (
                <Grid key={tpl.id} size={{ xs: 12, md: 6 }}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 2, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: currentBank.color + "18", color: currentBank.color, borderRadius: 2 }}>
                          <FilePresentIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={800}>
                            {tpl.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tpl.description}
                          </Typography>
                        </Box>
                      </Stack>
                      <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(tpl.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={tpl.fileName || "formulario.pdf"}
                          size="small"
                          variant="outlined"
                          color={tpl.fileUrl ? "success" : "default"}
                          sx={{ fontFamily: "monospace", fontWeight: 700 }}
                        />
                        {tpl.fileUrl ? (
                          <Chip label="PDF Subido" size="small" color="success" sx={{ fontSize: "0.65rem", fontWeight: 800 }} />
                        ) : (
                          <Chip label="Sin PDF Subido" size="small" color="warning" sx={{ fontSize: "0.65rem", fontWeight: 800 }} />
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <input
                          type="file"
                          id={`replace-file-${tpl.id}`}
                          hidden
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg"
                          onChange={(e) => handleReplaceTemplateFile(tpl.id, e.target.files?.[0])}
                        />

                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          variant="contained"
                          color="primary"
                          disabled={!tpl.fileUrl}
                          onClick={() => {
                            if (tpl.fileUrl) {
                              const resolvedUrl = getLocalFormFile(tpl.fileUrl) || tpl.fileUrl;
                              setViewingDocument({
                                title: tpl.title,
                                fileName: tpl.fileName || "formulario.pdf",
                                fileUrl: resolvedUrl,
                              });
                            } else {
                              if (window.confirm(`El formulario "${tpl.title}" aún no tiene un archivo subido. ¿Deseas subir el archivo ahora?`)) {
                                document.getElementById(`replace-file-${tpl.id}`)?.click();
                              }
                            }
                          }}
                          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                        >
                          Ver Documento
                        </Button>

                        <Button
                          size="small"
                          startIcon={replacingTemplateId === tpl.id ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
                          variant="outlined"
                          color="info"
                          disabled={replacingTemplateId === tpl.id}
                          onClick={() => document.getElementById(`replace-file-${tpl.id}`)?.click()}
                          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                        >
                          {replacingTemplateId === tpl.id ? "Subiendo..." : "Reemplazar"}
                        </Button>

                        {tpl.fileUrl && (
                          <Tooltip title="Descargar archivo a tu equipo">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownloadOrViewForm(tpl)}
                              sx={{ border: "1px solid #cbd5e1", borderRadius: 2 }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid size={{ xs: 12 }}>
                <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, bgcolor: "#fafafa" }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    No hay plantillas editables subidas para {currentBank.name}.
                  </Typography>
                  <Button startIcon={<CloudUploadIcon />} onClick={() => setOpenAddModal(true)} sx={{ mt: 1.5, textTransform: "none", fontWeight: 800 }}>
                    Subir Primera Plantilla
                  </Button>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Stack>
      )}

      {/* CONTENIDO TAB 1: MATRIZ GENERAL DE REQUISITOS (INDEPENDIENTE Y GENERAL PARA TODOS LOS BANCOS) */}
      {activeTab === 1 && (
        <Stack spacing={2.5}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: "#f8fafc" }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Chip label="Matriz Unificada Global" size="small" color="primary" sx={{ fontWeight: 800, fontSize: "0.72rem" }} />
                  <Chip label={`Aplica a los ${banks.length} Banco(s)`} size="small" color="success" variant="outlined" sx={{ fontWeight: 800, fontSize: "0.72rem" }} />
                </Stack>
                <Typography variant="h6" fontWeight={900} color="text.primary">
                  📋 Matriz General de Requisitos por Perfil Económico
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Define la lista oficial de documentos exigidos de manera general para todos los bancos según la actividad del cliente.
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel sx={{ fontWeight: 700 }}>Actividad Económica / Perfil</InputLabel>
                  <Select
                    value={selectedProfileId}
                    label="Actividad Económica / Perfil"
                    onChange={(e) => setSelectedProfileId(e.target.value)}
                    sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#fff" }}
                  >
                    {economicProfiles.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenProfileModal(true)}
                  sx={{ textTransform: "none", fontWeight: 800, whiteSpace: "nowrap", borderRadius: 2 }}
                >
                  + Nueva Actividad
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  disabled={economicProfiles.length <= 1}
                  onClick={() => {
                    const prof = economicProfiles.find((p) => p.id === selectedProfileId);
                    if (prof) setProfileToDelete(prof);
                  }}
                  sx={{ textTransform: "none", fontWeight: 800, whiteSpace: "nowrap", borderRadius: 2 }}
                >
                  Eliminar
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PostAddIcon />}
                  onClick={() => setOpenDocModal(true)}
                  sx={{ textTransform: "none", fontWeight: 800, whiteSpace: "nowrap", borderRadius: 2 }}
                >
                  + Nuevo Tipo Documento
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#0f172a" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Documento Exigido</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Categoría / Detalles</TableCell>
                  <TableCell align="center" sx={{ color: "#38bdf8", fontWeight: 800, width: 280 }}>
                    Requisito General (Todos los Bancos)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentTypes.map((docDef) => {
                  const currentRequiredDocs = globalMatrix[selectedProfileId] || [];
                  const isRequired = currentRequiredDocs.includes(docDef.id);

                  return (
                    <TableRow key={docDef.id} hover sx={{ bgcolor: isRequired ? "#f0fdf4" : "inherit" }}>
                      <TableCell sx={{ fontWeight: 800, fontSize: "13px" }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          {isRequired ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <CancelIcon color="disabled" fontSize="small" />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight={800}>
                              {docDef.label}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ fontSize: "12px", color: "text.secondary" }}>
                        <Chip
                          label={docDef.category}
                          size="small"
                          sx={{ mr: 1, fontSize: "0.65rem", fontWeight: 800, textTransform: "uppercase" }}
                        />
                        {docDef.description || "Sin descripción adicional"}
                      </TableCell>

                      <TableCell align="center">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isRequired}
                              onChange={() => handleToggleGlobalMatrixRule(docDef.id)}
                              color="success"
                              size="small"
                            />
                          }
                          label={
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              color={isRequired ? "success.main" : "text.secondary"}
                            >
                              {isRequired ? "REQUERIDO PARA TODOS" : "NO EXIGIDO"}
                            </Typography>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {/* CONTENIDO TAB 2: LECTURA DE DOCUMENTOS CON IA & AUTO-LLENADO */}
      {activeTab === 2 && (
        <Stack spacing={2.5}>
          <Alert severity="info" icon={<AutoAwesomeIcon />} sx={{ borderRadius: 2.5, "& .MuiAlert-message": { fontWeight: 600 } }}>
            El pipeline de <strong>IA Vision OCR</strong> analiza documentos escaneados (*Cédula, RUT, Extractos, Certificados*) y autocompleta el formulario oficial de <strong>{currentBank.name}</strong>.
          </Alert>

          <Grid container spacing={1.5}>
            {documentTypes.slice(0, 6).map((docItem) => (
              <Grid key={docItem.id} size={{ xs: 6, sm: 4, md: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={analyzing}
                  onClick={() => handleSimulateAIScan(docItem.label)}
                  startIcon={<FilePresentIcon />}
                  sx={{
                    borderRadius: 2.5,
                    py: 1.5,
                    flexDirection: "column",
                    gap: 0.5,
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: "0.75rem",
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    "&:hover": { borderColor: currentBank.color, bgcolor: currentBank.color + "0D" },
                  }}
                >
                  {docItem.label}
                </Button>
              </Grid>
            ))}
          </Grid>

          {analyzing ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
              <CircularProgress size={32} sx={{ color: currentBank.color, mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={800}>
                IA analizando estructura de {lastScannedDoc}... Extrayendo campos.
              </Typography>
            </Paper>
          ) : parsedProfile ? (
            <Card variant="outlined" sx={{ borderRadius: 3, p: 2.5, bgcolor: "#f8fafc", border: `1px solid ${currentBank.color}` }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} mb={2}>
                <Typography variant="subtitle1" fontWeight={900} color="primary">
                  ⚡ Perfil Extraído por IA para {parsedProfile.clientName}
                </Typography>

                <Button
                  variant="contained"
                  color="success"
                  startIcon={<BoltIcon />}
                  onClick={handleCopyScript}
                  sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
                >
                  Copiar Script Auto-Llenado {currentBank.name}
                </Button>
              </Stack>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Solicitante:</Typography>
                  <Typography variant="body2" fontWeight={800}>{parsedProfile.clientName}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Cédula / Documento:</Typography>
                  <Typography variant="body2" fontWeight={800}>{parsedProfile.documentNumber}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Ingresos Mensuales Extraídos:</Typography>
                  <Typography variant="body2" fontWeight={900} color="success.main">
                    ${parsedProfile.monthlyIncome.toLocaleString("es-CO")} COP
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          ) : null}
        </Stack>
      )}

      {/* Modal 1: Crear Nuevo Banco */}
      <Dialog open={openBankModal} onClose={() => setOpenBankModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>+ Crear Nuevo Banco o Entidad</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={1}>
            <TextField
              label="Nombre del Banco"
              placeholder="Ej: Banco de Occidente"
              fullWidth
              size="small"
              value={newBankName}
              onChange={(e) => setNewBankName(e.target.value)}
            />
            <TextField
              label="Código Corto (3-4 letras)"
              placeholder="Ej: OCCIDENTE"
              fullWidth
              size="small"
              value={newBankCode}
              onChange={(e) => setNewBankCode(e.target.value)}
            />
            <TextField
              label="Color Distintivo (HEX)"
              type="color"
              fullWidth
              size="small"
              value={newBankColor}
              onChange={(e) => setNewBankColor(e.target.value)}
            />
            <TextField
              label="Descripción"
              placeholder="Ej: Radicación de créditos comerciales"
              fullWidth
              size="small"
              value={newBankDesc}
              onChange={(e) => setNewBankDesc(e.target.value)}
            />
            <TextField
              label="URL Portal del Banco"
              placeholder="https://www.bancodeoccidente.com.co"
              fullWidth
              size="small"
              value={newBankPortal}
              onChange={(e) => setNewBankPortal(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBankModal(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleCreateBank} sx={{ fontWeight: 800 }}>
            Crear Banco
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 2: Crear Nuevo Tipo de Documento */}
      <Dialog open={openDocModal} onClose={() => setOpenDocModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>+ Crear Nuevo Tipo de Documento</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={1}>
            <TextField
              label="Nombre del Documento"
              placeholder="Ej: Paz y Salvo DIAN"
              fullWidth
              size="small"
              value={newDocLabel}
              onChange={(e) => setNewDocLabel(e.target.value)}
            />
            <TextField
              label="Descripción o Instrucción"
              placeholder="Ej: Certificado de paz y salvo tributario vigente"
              fullWidth
              size="small"
              value={newDocDesc}
              onChange={(e) => setNewDocDesc(e.target.value)}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Categoría</InputLabel>
              <Select
                value={newDocCategory}
                label="Categoría"
                onChange={(e) => setNewDocCategory(e.target.value)}
              >
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="laboral">Laboral</MenuItem>
                <MenuItem value="financiero">Financiero</MenuItem>
                <MenuItem value="empresa">Empresa</MenuItem>
                <MenuItem value="vehiculo">Vehículo</MenuItem>
                <MenuItem value="propiedad">Propiedad</MenuItem>
                <MenuItem value="otros">Otros</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDocModal(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreateDocumentType} sx={{ fontWeight: 800 }}>
            Crear Documento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 3: Subir Plantilla */}
      <Dialog open={openAddModal} onClose={() => !uploadingForm && setOpenAddModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Subir Formulario Editable a {currentBank.name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={1}>
            <Box
              sx={{
                p: 2.5,
                border: "2px dashed",
                borderColor: selectedFormFile ? "#22c55e" : "#cbd5e1",
                borderRadius: 3,
                textAlign: "center",
                bgcolor: selectedFormFile ? "#f0fdf4" : "#f8fafc",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": { borderColor: "#3b82f6", bgcolor: "#eff6ff" },
              }}
              onClick={() => document.getElementById("modal-file-input")?.click()}
            >
              <input
                type="file"
                id="modal-file-input"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSelectedFormFile(file);
                    if (!newTitle) setNewTitle(file.name.replace(/\.[^/.]+$/, ""));
                    if (!newFileName) setNewFileName(file.name);
                  }
                }}
              />
              <CloudUploadIcon sx={{ fontSize: 40, color: selectedFormFile ? "#16a34a" : "#64748b", mb: 0.5 }} />
              <Typography variant="subtitle2" fontWeight={800} color={selectedFormFile ? "success.main" : "text.primary"}>
                {selectedFormFile ? selectedFormFile.name : "Seleccionar Archivo (PDF, DOCX, XLSX)"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {selectedFormFile
                  ? `${(selectedFormFile.size / 1024 / 1024).toFixed(2)} MB - Listo para guardar`
                  : "Sube la plantilla editable oficial de este banco"}
              </Typography>
            </Box>

            <TextField
              label="Título del Formulario"
              placeholder="Ej: Solicitud Única de Crédito"
              fullWidth
              size="small"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <TextField
              label="Descripción corta"
              placeholder="Ej: Formulario oficial PDF editable"
              fullWidth
              size="small"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
            <TextField
              label="Nombre de Archivo Visible"
              placeholder="solicitud_oficial_banco.pdf"
              fullWidth
              size="small"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} disabled={uploadingForm}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddTemplate}
            disabled={uploadingForm || !newTitle}
            startIcon={uploadingForm ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            sx={{ fontWeight: 800 }}
          >
            {uploadingForm ? "Subiendo a Storage..." : "Guardar Formulario"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 4: Confirmación de Eliminación de Banco */}
      <Dialog
        open={Boolean(bankToDelete)}
        onClose={() => setBankToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 900, color: "error.main", display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteIcon color="error" />
          ¿Eliminar Banco?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
            ¿Estás seguro de que deseas eliminar el banco "{bankToDelete?.name}"?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Esta acción eliminará la carpeta del banco y sus {bankToDelete?.templates?.length || 0} plantilla(s) asociada(s). La matriz de documentos se actualizará en Firestore.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBankToDelete(null)} sx={{ fontWeight: 700, color: "text.secondary" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteBankConfirm}
            startIcon={<DeleteIcon />}
            sx={{ fontWeight: 800, borderRadius: 2 }}
          >
            Eliminar Banco
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 5: Visor de Documento / Previsualización Integrada */}
      {(() => {
        const rawUrl = viewingDocument ? (getLocalFormFile(viewingDocument.fileUrl) || viewingDocument.fileUrl) : "";
        const isImage = rawUrl.startsWith("data:image/") || rawUrl.match(/\.(jpg|jpeg|png|webp)(\?.*)?$/i);

        return (
          <DocumentViewerDialog
            open={Boolean(viewingDocument)}
            viewingDocument={viewingDocument}
            onClose={() => setViewingDocument(null)}
            isImage={Boolean(isImage)}
          />
        );
      })()}

      {/* Modal 6: Crear Nueva Actividad Económica */}
      <Dialog open={openProfileModal} onClose={() => setOpenProfileModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>+ Nueva Actividad Económica</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Nombre de la Actividad (Ej: Ganadero, Comerciante)"
              fullWidth
              size="small"
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
              autoFocus
            />
            {newProfileName && (
              <Typography variant="caption" color="text.secondary">
                Identificador del sistema: <code>{newProfileName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "_")}</code>
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenProfileModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreateProfile} disabled={!newProfileName.trim()} sx={{ fontWeight: 800 }}>
            Crear Actividad Económica
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 7: Confirmar Eliminación de Actividad Económica */}
      <Dialog open={Boolean(profileToDelete)} onClose={() => setProfileToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: "error.main", display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="error" /> ¿Eliminar Actividad Económica?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
            ¿Estás seguro de que deseas eliminar la actividad económica "{profileToDelete?.label}"?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Esta acción la eliminará de la Matriz General de Requisitos de todos los bancos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setProfileToDelete(null)} sx={{ fontWeight: 700, color: "text.secondary" }}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteProfileConfirm} sx={{ fontWeight: 800 }}>
            Sí, Eliminar Actividad
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
